"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { getRetryQueue, enqueueRequest, type QueuedRequest } from "@/lib/retry-queue";

interface BackgroundSyncOptions {
  enabled?: boolean;
  onSyncComplete?: (request: QueuedRequest) => void;
  onSyncError?: (request: QueuedRequest, error: Error) => void;
}

export function useBackgroundSync(options?: BackgroundSyncOptions) {
  const { enabled = true, onSyncComplete, onSyncError } = options ?? {};
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const queueRef = useRef(getRetryQueue());

  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = queueRef.current.subscribe((queue) => {
      setPendingCount(queue.length);
    });

    return () => {
      unsubscribe();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const handleOnline = () => {
      queueRef.current.retryFailed();
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [enabled]);

  const registerRequest = useCallback(
    (url: string, method: string, options?: { body?: unknown; headers?: Record<string, string> }): string => {
      return enqueueRequest({ url, method, ...options });
    },
    []
  );

  const syncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      await queueRef.current.retryFailed();
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const clearQueue = useCallback(() => {
    queueRef.current.clear();
    setPendingCount(0);
  }, []);

  return {
    pendingCount,
    isSyncing,
    registerRequest,
    syncNow,
    clearQueue,
  };
}